import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import logo from "../Assets/logo.svg";

const MANIFEST_URL = "/asset-manifest.json";
const MIN_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const POLL_INTERVAL_MS = 15 * 60 * 1000;

// Path of the main bundle this tab is actually running, read off the script tag
// index.html was served with. Using the live tag rather than a manifest fetched
// on mount avoids a race: a deploy landing between page load and that first
// fetch would otherwise be recorded as the baseline and never reported.
//
// Returns null under `npm start`, where the dev server serves an unhashed
// bundle and there is no new version to detect.
const loadedMainBundle = () => {
    const script = document.querySelector('script[src*="/static/js/main."]');
    if (!script) {
        return null;
    }
    return new URL(script.src, window.location.origin).pathname;
};

const fetchDeployedMainBundle = async () => {
    // no-store, or the check compares a cached manifest against itself.
    const response = await fetch(MANIFEST_URL, { cache: "no-store" });
    if (!response.ok) {
        throw new Error(`asset-manifest responded ${response.status}`);
    }
    const manifest = await response.json();
    return manifest?.files?.["main.js"] || null;
};

// Tells long-lived tabs that a deploy has happened.
//
// Cache headers alone can't reach a tab that has been open for days: nothing
// re-requests index.html until a reload, so the tab keeps running the bundle it
// booted with. This polls the deployed manifest when the tab regains focus and
// offers a reload — it never reloads on its own, since a partner mid-form would
// lose everything they had typed.
//
// A reception desk leaves this open on screen all day, and a tab that is never
// hidden gets no visibilitychange, so focus alone would only ever check once at
// mount. Hence the poll as well.
export default function useVersionCheck() {
    const lastCheckedAt = useRef(0);
    const prompted = useRef(false);

    useEffect(() => {
        const runningBundle = loadedMainBundle();
        if (!runningBundle) {
            return undefined;
        }

        let active = true;

        const check = async () => {
            if (prompted.current) {
                return;
            }
            const now = Date.now();
            if (now - lastCheckedAt.current < MIN_CHECK_INTERVAL_MS) {
                return;
            }
            lastCheckedAt.current = now;

            let deployedBundle;
            try {
                deployedBundle = await fetchDeployedMainBundle();
            } catch (error) {
                // Offline or a blip mid-deploy. Stay quiet and retry on the
                // next focus rather than surfacing an error the partner can do
                // nothing about.
                return;
            }

            if (!active || !deployedBundle || deployedBundle === runningBundle) {
                return;
            }

            // Latched: once dismissed, the prompt does not come back for the
            // life of the tab.
            prompted.current = true;
            toast.info(
                <div>
                    <p className="text-[14px] font-bold text-[#050F4E]">
                        New version available
                    </p>
                    <p className="mt-0.5 text-[12px] leading-snug text-[#8C9199]">
                        Refresh to get the latest updates
                    </p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="mt-2.5 w-full whitespace-nowrap rounded-lg bg-[#FF5A5F] px-3 py-2 text-sm font-bold text-white"
                    >
                        Refresh
                    </button>
                </div>,
                {
                    toastId: "app-update",
                    autoClose: false,
                    closeOnClick: false,
                    draggable: false,
                    className: "alfred-toast",
                    icon: <img src={logo} alt="" className="h-7 w-7" />,
                },
            );
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                check();
            }
        };

        // Also check once on mount: a browser still holding a stale index.html
        // boots an old bundle, and that tab should be offered the reload
        // without waiting for a focus change.
        check();

        // Left running while the tab is visible. The throttle above means a
        // tick landing just after a visibility check costs nothing.
        const poll = setInterval(() => {
            if (document.visibilityState === "visible") {
                check();
            }
        }, POLL_INTERVAL_MS);

        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            active = false;
            clearInterval(poll);
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };
    }, []);
}
